import moment from 'moment';
import { useCallback, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useLazyGetEmployeeRecordsQuery } from '@app/api/Records/api';
import { WARNING } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import {
  useCalendarSettingsContext,
  useIndividualCalendarContext,
} from '@app/providers/CalendarProvider/useCalendar';
import { handleCatchError } from '@app/services';

import { adaptModelEmployeeRecords } from '../utils/adaptModelEmployeeRecords';

export const useIndividualCalendarDataRefresh = () => {
  const [query] = useSearchParams();
  const { activeBranchId } = useUserPrint();
  const { showNotification } = useContext(NotificationContext);
  const { setIsLoading } = useCalendarSettingsContext();
  const { setIndividualCalendarData, setLastStoredIndividualCalendarData } =
    useIndividualCalendarContext();
  const [getEmployeeRecords] = useLazyGetEmployeeRecordsQuery();

  const employeeId = Number(query.get('employeeId'));
  const dateS =
    query.get('dateS') ??
    moment().subtract(2, 'weeks').startOf('isoWeek').format('YYYY-MM-DD');
  const dateE =
    query.get('dateE') ??
    moment().add(2, 'weeks').endOf('isoWeek').format('YYYY-MM-DD');

  const getEmployeeDataForTheSameQueryParams = useCallback(async () => {
    if (!employeeId || !activeBranchId || !dateS || !dateE) return;

    setIsLoading(true);

    try {
      const result = await getEmployeeRecords({
        employeeId,
        ActiveBranchId: activeBranchId,
        dateS,
        dateE,
      }).unwrap();
      const adaptedRecords = adaptModelEmployeeRecords(result);

      setIndividualCalendarData(adaptedRecords);
      setLastStoredIndividualCalendarData(adaptedRecords);
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    } finally {
      setIsLoading(false);
    }
  }, [
    activeBranchId,
    dateE,
    dateS,
    employeeId,
    setIndividualCalendarData,
    setIsLoading,
    setLastStoredIndividualCalendarData,
    showNotification,
    getEmployeeRecords,
  ]);

  return { getEmployeeDataForTheSameQueryParams };
};
